import React from 'react';
import BaseScadaNode from './BaseScadaNode';

export function TankSVG({ config = {}, width = 60, height = 80, liveValues = {} }) {
    const min = parseFloat(config.min_value ?? 0);
    const max = parseFloat(config.max_value ?? 100);
    const levelVal = liveValues.level;
    const levelBound = levelVal != null;
    const v = parseFloat(levelVal ?? 0);
    const pct = Math.min(1, Math.max(0, (v - min) / (max - min || 1)));

    const capCount = parseInt(config.level_cap_count ?? 0);
    // When capacitive sensors are present, leave 10px on the right for sensor dots
    const sensorMargin = capCount > 0 ? 12 : 0;

    const bodyX = 4;
    const bodyY = 8;
    const bodyW = width - 8 - sensorMargin;
    const bodyH = height - 16;
    const fillH = bodyH * pct;
    const fillY = bodyY + bodyH - fillH;
    const color = config.fill_color || '#3B82F6';
    const borderColor = config.border_color || '#94A3B8';
    const clipId = `tankClip_${width}_${height}_${capCount}`;

    // Sensor dots positions (evenly spaced bottom→top inside body)
    const sensorDots = capCount > 0 ? Array.from({ length: capCount }, (_, i) => {
        const key = `level_cap_${i + 1}`;
        const val = liveValues[key];
        const active = val != null && (val === true || val === 1 || val === '1' || val === 'true');
        const unbound = val == null;
        // Position: bottom sensor is 1st, top sensor is Nth
        const fraction = capCount === 1 ? 0.5 : i / (capCount - 1); // 0 = bottom, 1 = top
        const cy = bodyY + bodyH - (bodyH * fraction * 0.85 + bodyH * 0.075);
        return { key, active, unbound, cy };
    }) : [];

    const sensorX = bodyX + bodyW + 6;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Tank body */}
            <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx={4} fill="#0F172A" stroke={borderColor} strokeWidth={2} />

            {/* Fill level */}
            <clipPath id={clipId}>
                <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx={4} />
            </clipPath>
            {levelBound && (
                <rect x={bodyX} y={fillY} width={bodyW} height={fillH} fill={color} opacity={0.8} clipPath={`url(#${clipId})`} />
            )}

            {/* Value label — only when level signal is bound */}
            {levelBound && (
                <text x={bodyX + bodyW / 2} y={height / 2 + 4} textAnchor="middle" fill="#F1F5F9" fontSize={10} fontWeight="bold">
                    {isNaN(v) ? '--' : v.toFixed(config.decimals ?? 0)}{config.unit || ''}
                </text>
            )}

            {/* Top connector stub */}
            <rect x={bodyX + bodyW / 2 - 4} y={0} width={8} height={bodyY} rx={1} fill={borderColor} />
            {/* Bottom connector stub */}
            <rect x={bodyX + bodyW / 2 - 4} y={bodyY + bodyH} width={8} height={8} rx={1} fill={borderColor} />

            {/* Capacitive sensor dots on the right side */}
            {sensorDots.map(({ key, active, unbound, cy }) => (
                <g key={key}>
                    {/* Connector line to tank wall */}
                    <line x1={bodyX + bodyW} y1={cy} x2={sensorX - 3} y2={cy} stroke="#475569" strokeWidth={1} />
                    <circle
                        cx={sensorX} cy={cy} r={4}
                        fill={unbound ? '#1E293B' : active ? '#22C55E' : '#EF4444'}
                        stroke={unbound ? '#475569' : active ? '#16A34A' : '#DC2626'}
                        strokeWidth={1}
                    />
                </g>
            ))}
        </svg>
    );
}

export default function TankNode(props) {
    const { data } = props;
    const w = data?.width || 60;
    const h = data?.height || 80;
    return (
        <BaseScadaNode {...props}>
            <TankSVG config={data?.config} width={w} height={h} liveValues={data?.liveValues || {}} />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', marginTop: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
